import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  InputAdornment,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Calculate as CalculateIcon,
  AttachMoney as AttachMoneyIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Security as SecurityIcon,
  WaterDrop as WaterDropIcon,
  Agriculture as AgricultureIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import api from 'services/api';
import { pondsAPI, farmsAPI, waterReservoirsAPI, stockingEventsAPI } from 'services/api';
import { useAuth } from 'hooks/useAuth';

const PremiumCalculator = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    pond_id: '', // Optional: Select pond to auto-apply discounts
    species: 'P_VANNAMEI',
    plan: 'PLAN_A',
    doc_chosen: 120,
    sum_insured: 1800000,
    disease_doc: '',
    discount_factors: {
      good_farm_management: false,
      seed_selection: false,
      soil_water_pathogen_testing: false,
      water_reservoir_pond: false,
      epidemic_free_history: false,
      soil_type: false,
      stocking_density: false
    },
    loading_factors: {
      epidemic_incidence_history: false,
      non_spf_seeds_no_reservoir: false,
      region_of_stocking: false
    }
  });

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [planComparison, setPlanComparison] = useState(null);
  const [comparisonDialogOpen, setComparisonDialogOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [ponds, setPonds] = useState([]);
  const [farms, setFarms] = useState([]);
  const [checkingDiscounts, setCheckingDiscounts] = useState(false);
  const [autoAppliedDiscounts, setAutoAppliedDiscounts] = useState([]);

  const speciesOptions = [
    { value: 'P_VANNAMEI', label: 'White Leg Shrimp (P. vannamei)' },
    { value: 'P_INDICUS', label: 'Indian White Shrimp (P. indicus)' },
    { value: 'P_MONODON', label: 'Black Tiger Prawn (P. monodon)' },
    { value: 'M_ROSENBERGII', label: 'Scampi (M. rosenbergii)' }
  ];

  const planOptions = [
    { value: 'PLAN_A', label: 'Plan A - Basic Cover', description: 'Basic Perils Only' },
    { value: 'PLAN_B', label: 'Plan B - Comprehensive Cover', description: 'Basic Perils + Notified Diseases' },
    { value: 'PLAN_C', label: 'Plan C - Comprehensive with Additional Diseases', description: 'Plan B + Additional Diseases' }
  ];

  const docOptions = [
    { value: 60, label: '60 Days' },
    { value: 80, label: '80 Days' },
    { value: 100, label: '100 Days' },
    { value: 120, label: '120 Days' },
    { value: 130, label: '130 Days' }
  ];

  const diseaseDocOptions = [
    { value: 'disease_doc_60', label: 'Disease Coverage up to 60 DOC' },
    { value: 'disease_doc_80', label: 'Disease Coverage up to 80 DOC' },
    { value: 'disease_doc_100', label: 'Disease Coverage up to 100 DOC' },
    { value: 'disease_doc_120', label: 'Disease Coverage up to 120 DOC' }
  ];

  const discountOptions = [
    { key: 'good_farm_management', label: 'Good Farm Management Practices', percentage: '5%' },
    { key: 'seed_selection', label: 'SPF/SPR Seed Selection', percentage: '15%' },
    { key: 'soil_water_pathogen_testing', label: 'Soil & Water Pathogen Testing', percentage: '5%' },
    { key: 'water_reservoir_pond', label: 'Water Reservoir Pond Available', percentage: '5%' },
    { key: 'epidemic_free_history', label: 'Epidemic Free History (3 years)', percentage: '10%' },
    { key: 'soil_type', label: 'Favorable Soil Type', percentage: '10%' },
    { key: 'stocking_density', label: 'Optimal Stocking Density', percentage: '10%' }
  ];

  const loadingOptions = [
    { key: 'epidemic_incidence_history', label: 'History of Epidemic Incidence', percentage: '20%' },
    { key: 'non_spf_seeds_no_reservoir', label: 'Non-SPF Seeds + No Reservoir', percentage: '15%' },
    { key: 'region_of_stocking', label: 'High-Risk Region', percentage: '10%' }
  ];

  const steps = [
    'Select Species & Plan',
    'Set Coverage Period',
    'Configure Discounts & Loadings',
    'Calculate Premium'
  ];

  useEffect(() => {
    if (user) {
      loadPondsAndFarms();
    }
  }, [user]);

  const loadPondsAndFarms = async () => {
    try {
      const [pondsResponse, farmsResponse] = await Promise.all([
        pondsAPI.getAllForUser(),
        farmsAPI.getAll()
      ]);
      
      const userFarms = farmsResponse.data.filter(farm => farm.user_id === user.id);
      setPonds(pondsResponse);
      setFarms(userFarms);
    } catch (err) {
      console.error('Error loading ponds and farms:', err);
    }
  };

  const checkAndApplyAutoDiscounts = async (pondId) => {
    if (!pondId) {
      // Reset auto-applied discounts if pond is cleared
      setAutoAppliedDiscounts([]);
      return;
    }

    setCheckingDiscounts(true);
    try {
      const pond = ponds.find(p => p.id === parseInt(pondId));
      if (!pond) {
        setCheckingDiscounts(false);
        return;
      }

      const appliedDiscounts = [];
      const updatedDiscountFactors = { ...formData.discount_factors };

      // Check WaterReservoir for treatment facilities (5% discount)
      try {
        const farm = farms.find(f => f.id === pond.farm_id);
        if (farm) {
          const reservoirsResponse = await waterReservoirsAPI.getByFarm(farm.id);
          const reservoirs = reservoirsResponse.data || [];
          const hasTreatmentFacilities = reservoirs.some(r => r.treatment_facilities === true);
          
          if (hasTreatmentFacilities) {
            updatedDiscountFactors.water_reservoir_pond = true;
            appliedDiscounts.push('Water Reservoir (5%)');
          } else {
            updatedDiscountFactors.water_reservoir_pond = false;
          }
        }
      } catch (err) {
        console.error('Error checking water reservoirs:', err);
      }

      // Check StockingEvent for seed quality (SPF = 15%, SPR = 10%)
      try {
        const stockingEventsResponse = await stockingEventsAPI.getByPond(pondId);
        const stockingEvents = stockingEventsResponse.data || [];
        
        if (stockingEvents.length > 0) {
          // Get the most recent stocking event
          const mostRecentEvent = stockingEvents.sort((a, b) => 
            new Date(b.stocking_date) - new Date(a.stocking_date)
          )[0];
          
          // Check seed quality
          const seedQuality = mostRecentEvent.seed_quality || (mostRecentEvent.spf_seed ? 'SPF' : 'NON_SPF');
          
          if (seedQuality === 'SPF' || seedQuality === 'SPF_SEED') {
            updatedDiscountFactors.seed_selection = true;
            appliedDiscounts.push('SPF Seed Selection (15%)');
          } else if (seedQuality === 'SPR' || seedQuality === 'SPR_SEED') {
            updatedDiscountFactors.seed_selection = true;
            appliedDiscounts.push('SPR Seed Selection (10%)');
          } else {
            updatedDiscountFactors.seed_selection = false;
          }
        } else {
          updatedDiscountFactors.seed_selection = false;
        }
      } catch (err) {
        console.error('Error checking stocking events:', err);
      }

      setFormData(prev => ({
        ...prev,
        discount_factors: updatedDiscountFactors
      }));
      setAutoAppliedDiscounts(appliedDiscounts);
    } catch (err) {
      console.error('Error checking auto discounts:', err);
    } finally {
      setCheckingDiscounts(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDiscountChange = (key, checked) => {
    setFormData(prev => ({
      ...prev,
      discount_factors: {
        ...prev.discount_factors,
        [key]: checked
      }
    }));
  };

  const handleLoadingChange = (key, checked) => {
    setFormData(prev => ({
      ...prev,
      loading_factors: {
        ...prev.loading_factors,
        [key]: checked
      }
    }));
  };

  const calculatePremium = async () => {
    try {
      setLoading(true);
      setError(null);

      // Map plan from 'PLAN_A' to 'A', 'PLAN_B' to 'B', etc.
      const planMap = {
        'PLAN_A': 'A',
        'PLAN_B': 'B',
        'PLAN_C': 'C'
      };

      const requestData = {
        species: formData.species,
        plan: planMap[formData.plan] || formData.plan,
        doc_chosen: parseInt(formData.doc_chosen),
        sum_insured: parseFloat(formData.sum_insured),
        disease_doc: formData.plan === 'PLAN_B' ? formData.disease_doc : undefined,
        discount_factors: formData.discount_factors,
        loading_factors: formData.loading_factors
      };

      const response = await api.post('/api/v1/calculate-premium', requestData);
      
      if (response.data.success) {
        setResult(response.data.data);
        setActiveStep(3);
      } else {
        setError(response.data.error?.message || 'Failed to calculate premium');
      }
    } catch (err) {
      console.error('Error calculating premium:', err);
      setError('Failed to calculate premium. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const comparePlans = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.post('/api/v1/compare-plans', {
        species: formData.species,
        doc_chosen: parseInt(formData.doc_chosen),
        sum_insured: parseFloat(formData.sum_insured)
      });
      
      if (response.data.success) {
        setPlanComparison(response.data.data);
        setComparisonDialogOpen(true);
      } else {
        setError(response.data.error?.message || 'Failed to compare plans');
      }
    } catch (err) {
      console.error('Error comparing plans:', err);
      setError('Failed to compare plans. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getTotalDiscount = () => {
    const total = Object.entries(formData.discount_factors)
      .filter(([_, checked]) => checked)
      .reduce((sum, [key, _]) => {
        const option = discountOptions.find(opt => opt.key === key);
        return sum + parseFloat(option.percentage.replace('%', ''));
      }, 0);
    return Math.min(total, 40); // Cap at 40%
  };

  const getTotalLoading = () => {
    const total = Object.entries(formData.loading_factors)
      .filter(([_, checked]) => checked)
      .reduce((sum, [key, _]) => {
        const option = loadingOptions.find(opt => opt.key === key);
        return sum + parseFloat(option.percentage.replace('%', ''));
      }, 0);
    return Math.min(total, 30); // Cap at 30%
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Box p={3}>
      {/* Progress Stepper */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Form Section */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Step 1: Species & Insurance Plan */}
            <Card sx={{ 
              border: '1px solid #e3f2fd',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
            }}>
              <CardContent sx={{ p: 2.5 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                  Step 1: Species & Insurance Plan
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Shrimp Species</InputLabel>
                      <Select
                        value={formData.species}
                        onChange={(e) => handleInputChange('species', e.target.value)}
                        label="Shrimp Species"
                      >
                        {speciesOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Insurance Plan</InputLabel>
                      <Select
                        value={formData.plan}
                        onChange={(e) => handleInputChange('plan', e.target.value)}
                        label="Insurance Plan"
                      >
                        {planOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>{option.label}</Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {option.description}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Step 2: Coverage Period & Sum Insured */}
            <Card sx={{ 
              border: '1px solid #e3f2fd',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
            }}>
              <CardContent sx={{ p: 2.5 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                  Step 2: Coverage Period & Sum Insured
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Select Pond (Optional - Auto-apply discounts)</InputLabel>
                      <Select
                        value={formData.pond_id}
                        onChange={async (e) => {
                          const pondId = e.target.value;
                          handleInputChange('pond_id', pondId);
                          await checkAndApplyAutoDiscounts(pondId);
                        }}
                        label="Select Pond (Optional - Auto-apply discounts)"
                      >
                        <MenuItem value="">
                          <em>None - Manual Entry</em>
                        </MenuItem>
                        {ponds.map((pond) => {
                          const farm = farms.find(f => f.id === pond.farm_id);
                          return (
                            <MenuItem key={pond.id} value={pond.id}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {pond.pond_name || pond.name || `Pond ${pond.id}`}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {farm?.name || 'Unknown Farm'} • {pond.species || 'N/A'}
                                </Typography>
                              </Box>
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                    {checkingDiscounts && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        <Typography variant="caption" color="text.secondary">
                          Checking for automatic discounts...
                        </Typography>
                      </Box>
                    )}
                    {autoAppliedDiscounts.length > 0 && (
                      <Alert severity="success" sx={{ mt: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Auto-applied Discounts:
                        </Typography>
                        {autoAppliedDiscounts.map((discount, idx) => (
                          <Typography key={idx} variant="caption" display="block">
                            ✓ {discount}
                          </Typography>
                        ))}
                      </Alert>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Days of Culture (DOC)</InputLabel>
                      <Select
                        value={formData.doc_chosen}
                        onChange={(e) => handleInputChange('doc_chosen', parseInt(e.target.value) || 120)}
                        label="Days of Culture (DOC)"
                      >
                        {docOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Sum Insured"
                      type="number"
                      value={formData.sum_insured}
                      onChange={(e) => handleInputChange('sum_insured', parseFloat(e.target.value) || 0)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      }}
                    />
                  </Grid>
                </Grid>

                {/* Disease DOC for Plan B */}
                {formData.plan === 'PLAN_B' && (
                  <Box mt={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Disease Coverage Period</InputLabel>
                      <Select
                        value={formData.disease_doc}
                        onChange={(e) => handleInputChange('disease_doc', e.target.value)}
                        label="Disease Coverage Period"
                      >
                        {diseaseDocOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Step 3: Discounts & Loadings */}
            <Card sx={{ 
              border: '1px solid #e3f2fd',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
            }}>
              <CardContent sx={{ p: 2.5 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                  Step 3: Discounts & Loadings
                </Typography>
                
                {/* Discounts */}
                <Box mb={2}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                      Available Discounts (Total: {getTotalDiscount()}% / 40% max)
                    </Typography>
                    {autoAppliedDiscounts.length > 0 && (
                      <Chip 
                        label={`${autoAppliedDiscounts.length} Auto-applied`} 
                        size="small" 
                        color="success" 
                        icon={<CheckCircleIcon />}
                      />
                    )}
                  </Box>
                  <Grid container spacing={1.5}>
                    {discountOptions.map((option) => {
                      const isAutoApplied = 
                        (option.key === 'water_reservoir_pond' && autoAppliedDiscounts.some(d => d.includes('Water Reservoir'))) ||
                        (option.key === 'seed_selection' && autoAppliedDiscounts.some(d => d.includes('Seed Selection')));
                      
                      return (
                        <Grid item xs={12} sm={6} key={option.key}>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            p: 1,
                            borderRadius: 1,
                            bgcolor: formData.discount_factors[option.key] ? 'success.50' : 'transparent',
                            border: formData.discount_factors[option.key] ? '1px solid' : '1px solid transparent',
                            borderColor: formData.discount_factors[option.key] ? 'success.main' : 'transparent',
                            position: 'relative'
                          }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={formData.discount_factors[option.key]}
                                  onChange={(e) => {
                                    handleDiscountChange(option.key, e.target.checked);
                                    // Remove from auto-applied if manually toggled off
                                    if (!e.target.checked && isAutoApplied) {
                                      setAutoAppliedDiscounts(prev => 
                                        prev.filter(d => !d.includes(option.key === 'water_reservoir_pond' ? 'Water Reservoir' : 'Seed Selection'))
                                      );
                                    }
                                  }}
                                  color="success"
                                  size="small"
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                    {option.label}
                                  </Typography>
                                  {isAutoApplied && formData.discount_factors[option.key] && (
                                    <Typography variant="caption" color="success.main" display="block">
                                      Auto-applied from pond data
                                    </Typography>
                                  )}
                                </Box>
                              }
                              sx={{ m: 0, flex: 1 }}
                            />
                            <Chip 
                              label={option.percentage} 
                              size="small" 
                              color="success" 
                              sx={{ 
                                height: 24,
                                fontSize: '0.75rem',
                                fontWeight: 600
                              }} 
                            />
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Loadings */}
                <Box>
                  <Typography variant="body2" gutterBottom sx={{ fontWeight: 600, color: 'warning.main', mb: 1.5 }}>
                    Applicable Loadings (Total: {getTotalLoading()}% / 30% max)
                  </Typography>
                  <Grid container spacing={1.5}>
                    {loadingOptions.map((option) => (
                      <Grid item xs={12} sm={6} key={option.key}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          p: 1,
                          borderRadius: 1,
                          bgcolor: formData.loading_factors[option.key] ? 'warning.50' : 'transparent',
                          border: formData.loading_factors[option.key] ? '1px solid' : '1px solid transparent',
                          borderColor: formData.loading_factors[option.key] ? 'warning.main' : 'transparent'
                        }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={formData.loading_factors[option.key]}
                                onChange={(e) => handleLoadingChange(option.key, e.target.checked)}
                                color="warning"
                                size="small"
                              />
                            }
                            label={
                              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                {option.label}
                              </Typography>
                            }
                            sx={{ m: 0, flex: 1 }}
                          />
                          <Chip 
                            label={option.percentage} 
                            size="small" 
                            color="warning" 
                            sx={{ 
                              height: 24,
                              fontSize: '0.75rem',
                              fontWeight: 600
                            }} 
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <Box display="flex" gap={2} justifyContent="center" sx={{ mt: 1 }}>
              <Button
                variant="outlined"
                size="medium"
                onClick={comparePlans}
                disabled={loading}
                startIcon={<AssessmentIcon />}
                sx={{ borderRadius: 1.5 }}
              >
                COMPARE ALL PLANS
              </Button>
              <Button
                variant="contained"
                size="medium"
                onClick={calculatePremium}
                disabled={loading || (formData.plan === 'PLAN_B' && !formData.disease_doc)}
                startIcon={loading ? <CircularProgress size={18} /> : <CalculateIcon />}
                sx={{ borderRadius: 1.5 }}
              >
                {loading ? 'Calculating...' : 'CALCULATE PREMIUM'}
              </Button>
            </Box>
          </Box>
        </Grid>

        {/* Results Section */}
        <Grid item xs={12}>
          {result && (
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h5" component="h2" gutterBottom color="primary" fontWeight="bold">
                <CheckCircleIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'success.main' }} />
                Premium Calculation Result
              </Typography>

              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                    <CardContent>
                      <Typography variant="h4" fontWeight="bold">
                        {formatCurrency(result.final_premium)}
                      </Typography>
                      <Typography variant="body2">
                        Final Premium (Including GST)
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" color="primary">
                        {result.base_premium_rate}%
                      </Typography>
                      <Typography variant="caption">
                        Base Rate
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" color="success.main">
                        {result.discount_details.total_discount_percent}%
                      </Typography>
                      <Typography variant="caption">
                        Total Discount
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Detailed Breakdown */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Detailed Breakdown
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>Base Premium</TableCell>
                          <TableCell align="right">{formatCurrency(result.base_premium)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Discount Amount</TableCell>
                          <TableCell align="right" sx={{ color: 'success.main' }}>
                            -{formatCurrency(result.discount_amount)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Loading Amount</TableCell>
                          <TableCell align="right" sx={{ color: 'warning.main' }}>
                            +{formatCurrency(result.loading_amount)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Net Premium</TableCell>
                          <TableCell align="right">{formatCurrency(result.net_premium)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>GST ({result.gst_rate}%)</TableCell>
                          <TableCell align="right">{formatCurrency(result.gst_amount)}</TableCell>
                        </TableRow>
                        <TableRow sx={{ borderTop: 2, borderColor: 'primary.main' }}>
                          <TableCell><strong>Final Premium</strong></TableCell>
                          <TableCell align="right"><strong>{formatCurrency(result.final_premium)}</strong></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>

              {/* Copy Result */}
              <Box mt={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  onClick={() => copyToClipboard(`Premium Calculation Result:\nFinal Premium: ${formatCurrency(result.final_premium)}\nBase Rate: ${result.base_premium_rate}%\nDiscount: ${result.discount_details.total_discount_percent}%\nLoading: ${result.loading_details.total_loading_percent}%`)}
                >
                  Copy Result
                </Button>
              </Box>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Plan Comparison Dialog */}
      <Dialog 
        open={comparisonDialogOpen} 
        onClose={() => setComparisonDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Insurance Plan Comparison</Typography>
            <IconButton onClick={() => setComparisonDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {planComparison && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Plan</TableCell>
                    <TableCell>Coverage</TableCell>
                    <TableCell>Premium Rate</TableCell>
                    <TableCell>Final Premium</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Typography variant="subtitle2" color="primary">
                        {planComparison.plan_a.plan_name}
                      </Typography>
                    </TableCell>
                    <TableCell>{planComparison.plan_a.coverage}</TableCell>
                    <TableCell>{planComparison.plan_a.base_premium_rate}%</TableCell>
                    <TableCell><strong>{formatCurrency(planComparison.plan_a.final_premium)}</strong></TableCell>
                  </TableRow>
                  {Object.entries(planComparison.plan_b).map(([key, plan]) => (
                    <TableRow key={key}>
                      <TableCell>
                        <Typography variant="subtitle2" color="error">
                          {plan.plan_name}
                        </Typography>
                      </TableCell>
                      <TableCell>{plan.coverage}</TableCell>
                      <TableCell>{plan.base_premium_rate}%</TableCell>
                      <TableCell><strong>{formatCurrency(plan.final_premium)}</strong></TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell>
                      <Typography variant="subtitle2" color="info.main">
                        {planComparison.plan_c.plan_name}
                      </Typography>
                    </TableCell>
                    <TableCell>{planComparison.plan_c.coverage}</TableCell>
                    <TableCell>{planComparison.plan_c.base_premium_rate}%</TableCell>
                    <TableCell><strong>{formatCurrency(planComparison.plan_c.final_premium)}</strong></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComparisonDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PremiumCalculator;
